/**
 * Configuração de interceptadores HTTP com Axios
 * Responsabilidade única: configurar request/response interceptors
 */
import axios, { AxiosInstance } from 'axios';
import { getTenantFromWindowPathname, resolveTenantFromWindow } from '@/lib/tenant';
import {
  handleForbiddenError,
  handleUnauthorizedError,
  HttpErrorContext,
} from './http-error-handler';

let installed = false;

/**
 * Configura os interceptadores HTTP do Axios
 * - Request: adiciona tenant header
 * - Response: trata erros HTTP
 */
export function setupHttpInterceptors(instance: AxiosInstance = axios): void {
  if (installed) return;

  // Request interceptor: adiciona tenant header
  instance.interceptors.request.use((config) => {
    const requestUrl = String(config.url ?? '');
    const isApiRequest = requestUrl.startsWith('/api');

    if (!isApiRequest) {
      return config;
    }

    const tenant = getTenantFromWindowPathname();
    if (!tenant) {
      return config;
    }

    config.headers = {
      ...(config.headers ?? {}),
      'X-Tenant-Id': tenant,
    };

    return config;
  });

  // Response interceptor: trata erros
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const requestUrl = String(error?.config?.url ?? '');
      const status = error?.response?.status;
      const { tenant, source } = resolveTenantFromWindow();

      const context: HttpErrorContext = {
        error,
        requestUrl,
        tenant,
        tenantSource: source,
      };

      // Handler por status code
      switch (status) {
        case 403:
          handleForbiddenError(context);
          break;
        case 401:
          handleUnauthorizedError(context);
          break;
        default:
          // Outros erros são propagados normalmente
          break;
      }

      return Promise.reject(error);
    }
  );

  installed = true;
}
